import React, { useState, useEffect, useMemo  } from "react";
import { Link, Outlet, useNavigate, useParams } from "react-router-dom";
import Footer from "../common/Footer";
import HeaderPracticeQuestions from "../common/HeaderPracticeQuestions";
import useAuth from "../../hooks/useAuth";
import axios from "axios";
import { organNames } from "../../helpers";
import level2Data from "../../data/level2.json";
import level3Data from "../../data/level3.json";
import level4Data from "../../data/level4.json";
import DeleteQuestionModal from "../../modal/DeleteQuestionModal";

function PracticeQuestionsList() {
  const { user } = useAuth();
  const { level1, level2, level3, level4, level5 } = useParams();
  const navigate = useNavigate();

  const [showAnswer, setShowAnswer] = useState({});
  const [qBank, setqBank] = useState({});
  const [activeTab, setActiveTab] = useState(null);
  const [loading, setLoading] = useState(true);  
  const [showModal, setShowModal] = useState({
    show: false,
    id: null,
  });
  const sortedTabs = useMemo(() => {
  if (!qBank || typeof qBank !== "object") return [];
  return Object.keys(qBank).sort((a, b) => a.localeCompare(b));
}, [qBank]);

  const payload = { level1, level2, level3, level4, level5 };

  useEffect(() => {
    setShowAnswer({});
  }, [level1, level2, level3, level4]);

  const fetchQuestions = async () => {
    setLoading(true);
    axios
      .post(
        `${process.env.REACT_APP_API_URL}/practice-question-list`,
        payload,
        { headers: { Authorization: "Bearer " + user.jwtToken } }
      )
      .then((response) => {
        const data = response.data;
        setqBank(data);
        setLoading(false);
        
        if (!Array.isArray(data) && typeof data === "object") {
          const tabs = Object.keys(data).sort((a, b) => a.localeCompare(b));
          setActiveTab(tabs[0]);
        }
      });
  };

  useEffect(() => {
    if (user?.jwtToken) {
      fetchQuestions();
    }
  }, [user, level1, level2, level3, level4, level5]);

  const handleDelete = (id) => {
    setShowModal({ show: true, id });
  };

  // 🔥 Detect grouped response
  const isGrouped =
    !Array.isArray(qBank) &&
    typeof qBank === "object" &&
    Object.keys(qBank || {}).length > 0;

  const isEmpty =
    (Array.isArray(qBank) && qBank.length === 0) ||
    (!Array.isArray(qBank) && Object.keys(qBank || {}).length === 0);

function normalizeQuestionHtml(html) {
  if (!html) return html;

  // 1️⃣ Remove empty <strong></strong>
  html = html.replace(/<strong>\s*<\/strong>/gi, "");

  // 2️⃣ Extract content inside <p>...</p>
  const match = html.match(/<p>(.*?)<\/p>/is);
  if (!match) return html;

  let content = match[1];

  // 3️⃣ Remove ALL inner <strong> tags but KEEP text
  content = content.replace(/<\/?strong>/gi, "");

  // 4️⃣ Trim extra whitespace
  content = content.trim();

  // 5️⃣ Wrap ONCE with <strong>
  return `<p><strong>${content}</strong></p>`;
}

  return (
    <>
      {showModal.show && (
        <DeleteQuestionModal
          modal={showModal.show}
          setModal={setShowModal}
          id={showModal.id}
          fetchQuestions={fetchQuestions}
        />
      )}

      <div className="bg-green5 page-wrapper w-100 min-vh-100 d-flex flex-column">
        <HeaderPracticeQuestions />

        <main className="main-section mx-4 rounded-top-3 bg-white flex-grow-1 d-flex flex-column">

          {/* ================= TOP HEADER ================= */}
          <div className="row bg-green4 rounded-top-3 m-0 content-header position-relative">
            <div className="position-absolute d-flex w-100 top-0 start-50 translate-middle-x align-items-center justify-content-center text-green1 mt-1">
              <Link
                to={`/${level1}/${level2}/${level3}/${level4}`}
                className="btn btn-link text-decoration-none fw-bold"
              >
                <h5 className="fw-bold">
                  {organNames(level2Data, level1, level2)} |{" "}
                  {level3Data[`${level1}/${level2}/${level3}`]?.version}{" "}
                  {level3Data[`${level1}/${level2}/${level3}`]?.name} |{" "}
                  {level4Data[`${level1}/${level2}/${level3}/${level4}`]?.name}
                </h5>
              </Link>
            </div>
          </div>

          {/* ================= QUESTION ANSWER UI ================= */}
          <div className="d-flex justify-content-between align-items-center mt-2 ms-3 me-3">
            <h5 className="fw-bold mb-0">Practice Questions</h5>

            {user.role === "Admin" && (
              <Link
                className="btn btn-green3"
                to={
                  isGrouped && activeTab
                    ? `/${level1}/${level2}/${level3}/${level4}/practice-questions/add?tab=${encodeURIComponent(activeTab)}`
                    : `/${level1}/${level2}/${level3}/${level4}/practice-questions/add`
                }
              >
                Add
              </Link>
            )}
          </div>
          {loading && (
            <div className="flex-grow-1 d-flex align-items-center justify-content-center text-muted">
              <div className="spinner-border spinner-border-sm" />
              <span>Loading...</span>
            </div>
          )}


          {/* ================= EMPTY STATE ================= */}
          {!loading && isEmpty ? (
            <div className="flex-grow-1 d-flex align-items-center justify-content-center text-muted">
              No questions found
            </div>
          ) : (
            <div className="px-4 py-4">

              {/* ================= TABS ================= */}
              {isGrouped && (
                <>
                  <ul className="nav nav-tabs mb-3">
                    {sortedTabs.map((tabName) => (
                      <li className="nav-item" key={tabName}>
                        <button
                          className={`nav-link ${activeTab === tabName ? "active" : ""}`}
                          onClick={() => setActiveTab(tabName)}
                        >
                          <strong>{tabName}</strong>
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {/* ================= QUESTIONS ================= */}
              {(Array.isArray(qBank)
                ? qBank
                : qBank[activeTab] || []
              ).map((item, index) => (
                <div key={index} className="card mb-4 shadow-sm position-relative">

                  {user.role === "Admin" && (
                    <div className="position-absolute top-0 end-0 p-2">
                      <Link
                        to={`/${level1}/${level2}/${level3}/${level4}/practice-questions/edit/${item.id}`}
                        className="btn btn-green3 btn-sm text-secondary me-2"
                      >
                        <i className="bi bi-pencil-square"></i>
                      </Link>

                      <button
                        className="btn btn-green3 btn-sm"
                        onClick={() => handleDelete(item.id)}
                      >
                        <i className="bi bi-trash3"></i>
                      </button>
                    </div>
                  )}

                  <div className="card-body">
                    <div className="mt-1 flex items-start gap-2">
                      <span className="fw-bold me-2" style={{ float: "inline-start" }}>
                        Q{index + 1}.
                      </span>
                      <span dangerouslySetInnerHTML={{ __html: normalizeQuestionHtml(item.question) }} />
                    </div>

                    {Array.isArray(item.options) && (
                      <ul className="list-group mb-3">
                        {item.options.map((opt, i) => (
                          <li key={i} className="list-group-item d-flex">
                            <strong className="me-2">
                              {String.fromCharCode(65 + i)}.
                            </strong>
                            {opt}
                          </li>
                        ))}
                      </ul>
                    )}

                    <button
                      className="btn btn-outline-success btn-sm btn-green1"
                      onClick={() =>
                        setShowAnswer(prev => ({
                          ...prev,
                          [`/${level1}/${level2}/${level3}/${level4}-${activeTab || "default"}-${index}`]:
                            !prev[`/${level1}/${level2}/${level3}/${level4}-${activeTab || "default"}-${index}`],
                        }))
                      }
                    >
                      {showAnswer[`/${level1}/${level2}/${level3}/${level4}-${activeTab || "default"}-${index}`]
                        ? "Hide Answer"
                        : "Show Answer"}
                    </button>

                    {showAnswer[`/${level1}/${level2}/${level3}/${level4}-${activeTab || "default"}-${index}`] && (
                      <>
                        <div className="alert alert-success mt-3 mb-2">
                          <strong>Answer:</strong>{" "}
                          {(() => {
                            const answerIndex = Number(item.correctAnswer.key);
                            const label = String.fromCharCode(65 + answerIndex);
                            return `${label}. ${item.correctAnswer.text}`;
                          })()}
                        </div>

                        {item.explanation && (
                          <div className="alert alert-info">
                            <strong>Explanation:</strong>
                            <div
                              dangerouslySetInnerHTML={{
                                __html: item.explanation,
                              }}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="pt-3 row d-flex justify-content-center">
                    {level4Data[`${level1}/${level2}/${level3}/${level4}`] &&
                      level4Data[`${level1}/${level2}/${level3}/${level4}`]
                        .nav ? (
                      <>
                        <div className="col-12 col-xl-6 mb-2 d-flex justify-content-center justify-content-xl-start">
                          <Link
                            className="btn btn-green1 btn-sm fw-bold"
                             to={`/${level4Data[
                                `${level1}/${level2}/${level3}/${level4}`
                              ].nav[0]}/practice-questions`}
                          >
                            <span>
                              <span className="icon-left fs-6"></span>
                              {level4Data[
                                level4Data[
                                  `${level1}/${level2}/${level3}/${level4}`
                                ].nav[0]
                              ]
                                ?
                                level4Data[
                                    `${level1}/${level2}/${level3}/${level4}`
                                  ].nav[0].includes("chest")
                                ?
                                `${level4Data[
                                  level4Data[
                                    `${level1}/${level2}/${level3}/${level4}`
                                  ].nav[0]
                                ].version.replace(/\.1$/, "")
                                } ${level4Data[
                                  level4Data[
                                    `${level1}/${level2}/${level3}/${level4}`
                                  ].nav[0]
                                ].name
                                }`
                                :
                                `${level4Data[
                                  level4Data[
                                    `${level1}/${level2}/${level3}/${level4}`
                                  ].nav[0]
                                ].version
                                } ${level4Data[
                                  level4Data[
                                    `${level1}/${level2}/${level3}/${level4}`
                                  ].nav[0]
                                ].name
                                }`
                                : ""}
                            </span>
                          </Link>
                        </div>
                        <div className="col-12 col-xl-6 mb-2 d-flex justify-content-center justify-content-xl-end">
                          <Link
                            className="btn btn-green1 btn-sm fw-bold"
                              to={`/${level4Data[
                                `${level1}/${level2}/${level3}/${level4}`
                              ].nav[1]}/practice-questions`}
                          >
                            <span>
                              {level4Data[
                                level4Data[
                                  `${level1}/${level2}/${level3}/${level4}`
                                ].nav[1]
                              ]
                                ? 
                                level4Data[
                                  `${level1}/${level2}/${level3}/${level4}`
                                ].nav[1].includes("chest")
                                ?
                                `${level4Data[
                                  level4Data[
                                    `${level1}/${level2}/${level3}/${level4}`
                                  ].nav[1]
                                ].version.replace(/\.1$/, "")
                                } ${level4Data[
                                  level4Data[
                                    `${level1}/${level2}/${level3}/${level4}`
                                  ].nav[1]
                                ].name
                                }`
                                :
                                `${level4Data[
                                  level4Data[
                                    `${level1}/${level2}/${level3}/${level4}`
                                  ].nav[1]
                                ].version
                                } ${level4Data[
                                  level4Data[
                                    `${level1}/${level2}/${level3}/${level4}`
                                  ].nav[1]
                                ].name
                                }`
                                : ""}
                              <span className="icon-right fs-6"></span>
                            </span>
                          </Link>
                        </div>
                      </>
                    ) : (
                      ""
                    )}
                  </div>
          <Outlet />
        </main>

        <Footer />
      </div>
    </>
  );
}

export default PracticeQuestionsList;
